import {
  Box,
  Button,
  ControllerDataGrid,
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  MdDelete,
  MdEdit,
  Typography,
} from '@bymason-private/legacy-pattern-components';
import { NotificationModel } from '@bymason/controller-api-hooks';
import { getDataSize } from '@bymason/utils';

const LEVEL_API_TO_FE_MAP = new Map();
LEVEL_API_TO_FE_MAP.set('account-sim', 'Any SIM in account');
LEVEL_API_TO_FE_MAP.set('account', 'Account total');

interface NotificationsRenderProps {
  notifications?: NotificationModel[];
  notificationsBusy?: boolean;
  onDeleteNotification: (notification: NotificationModel) => void;
  onEditNotification: (notification: NotificationModel) => void;
}

export function NotificationsList({
  notifications,
  notificationsBusy,
  onDeleteNotification,
  onEditNotification,
}: NotificationsRenderProps) {
  const RenderActionButtons = (params: GridRenderCellParams) => {
    const notification = params.row.action as NotificationModel;
    const handleDeleteButtonClick = () => onDeleteNotification(notification);
    const handleEditButtonClick = () => onEditNotification(notification);

    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button color='primary' startIcon={<MdEdit size='12' />} onClick={handleEditButtonClick}>
          Edit
        </Button>
        <Button color='error' startIcon={<MdDelete size='12' />} onClick={handleDeleteButtonClick}>
          Delete
        </Button>
      </Box>
    );
  };

  const gridColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
    },
    {
      field: 'usageThreshold',
      headerName: 'Usage Threshold',
      flex: 1,
      valueFormatter: (x) => getDataSize(x.value).join(' '),
    },
    {
      field: 'level',
      headerName: 'Level',
      flex: 1,
      valueFormatter: (x) => LEVEL_API_TO_FE_MAP.get(x.value) ?? x.value,
    },
    {
      field: 'recipients',
      headerName: 'Recipients',
      flex: 1,
    },
    {
      field: 'action',
      headerName: 'Action',
      renderCell: RenderActionButtons,
      minWidth: 220,
      sortable: false,
      align: 'right',
    },
  ];

  const gridRows: GridRowsProp = notifications
    ? notifications.map((notification) => ({
        name: notification.name,
        description: notification.description,
        usageThreshold: notification.condition.value,
        level: notification.domain.type,
        recipients: notification.recipients.length,
        action: notification,
        id: notification.id,
      }))
    : [];

  return (
    <ControllerDataGrid
      sx={{ mt: 2 }}
      columns={gridColumns}
      disableSelectionOnClick
      disableColumnResize={false}
      components={{
        NoRowsOverlay: NoNotificationsOverlay,
      }}
      loading={notificationsBusy}
      rows={gridRows}
      density='standard'
    />
  );
}

const NoNotificationsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <Typography fontWeight='bold' color='grayText'>
      No notifications configured
    </Typography>
  </Box>
);
